import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Cake, Cookie, Candy, Carrot, Tent, UtensilsCrossed, Coffee, Droplet, Beef, Milk, Baby, Croissant, Soup, Sprout, Fish, Apple, Wheat, Nut, Pizza, Sandwich, HelpCircle, Leaf } from "lucide-react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { MobileNavigation } from "@/components/MobileNavigation";
import { DesktopNavigation } from "@/components/DesktopNavigation";

const foodGroupIcons = {
  'Baked Foods': Cake,
  'Snacks': Cookie, 
  'Sweets': Candy,
  'Vegetables': Carrot,
  'American Indian': Tent,
  'Restaurant Foods': UtensilsCrossed,
  'Beverages': Coffee,
  'Fats and Oils': Droplet,
  'Meats': Beef,
  'Dairy and Egg Products': Milk,
  'Baby Foods': Baby,
  'Breakfast Cereals': Croissant,
  'Soups and Sauces': Soup,
  'Beans and Lentils': Sprout,
  'Fish': Fish,
  'Fruits': Apple,
  'Grains and Pasta': Wheat,
  'Nuts and Seeds': Nut,
  'Prepared Meals': Pizza,
  'Fast Foods': Sandwich,
  'Spices and Herbs': Leaf
} as const;

const NutritionFacts = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: nutrition, isLoading } = useQuery({
    queryKey: ['nutrition', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID provided');
      const { data, error } = await supabase
        .from('nutrition_facts')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const nutrients = [
    { key: 'vitamin_a_iu_iu', label: 'Vitamin A', unit: 'IU', dailyValue: 5000 },
    { key: 'vitamin_c_mg', label: 'Vitamin C', unit: 'mg', dailyValue: 60 },
    { key: 'calcium_mg', label: 'Calcium', unit: 'mg', dailyValue: 1000 },
    { key: 'iron_fe_mg', label: 'Iron', unit: 'mg', dailyValue: 18 },
    { key: 'potassium_k_mg', label: 'Potassium', unit: 'mg', dailyValue: 4700 },
  ];

  if (isLoading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  if (!nutrition) {
    return <div className="text-center mt-8">Nutrition facts not found</div>;
  }

  const getNumericValue = (value: any): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculatePercentage = (value: number, dailyValue: number): number => {
    return Math.round((value / dailyValue) * 100) || 0;
  };
  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <DesktopNavigation />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{nutrition.name}</h1>
        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1 mb-6">
          {(() => {
            const IconComponent = foodGroupIcons[nutrition.food_group as keyof typeof foodGroupIcons] || HelpCircle;
            return <IconComponent className="h-4 w-4" />;
          })()}
          <span>{nutrition.food_group}</span>
        </div>

        <Card className="p-4 font-['Helvetica'] max-w-md mx-auto bg-white text-black">
          <div className="border-b-8 border-black pb-1">
            <h1 className="text-4xl font-bold">Nutrition Facts</h1>
            <div className="mt-1">
              <p className="text-sm">Serving Size</p>
              <p className="text-lg font-bold">
                {nutrition.serving_description_1_g || '1 serving'} ({getNumericValue(nutrition.serving_weight_1_g) || 100}g)
              </p>
            </div>
          </div>

          <div className="border-b-8 border-black py-2">
            <div className="flex justify-between items-baseline">
              <h2 className="text-3xl">Calories</h2>
              <span className="text-4xl font-bold">{getNumericValue(nutrition.calories)}</span>
            </div>
          </div>

          <div className="text-sm border-b border-black py-1">
            <p className="text-right font-bold">% Daily Value*</p>
          </div>

          <div className="border-b border-black">
            <div className="flex justify-between py-1">
              <span>Total Fat {getNumericValue(nutrition.fat_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.fat_g), 65)}%</span>
            </div>
            <div className="flex justify-between py-1 pl-4">
              <span>Saturated Fat {getNumericValue(nutrition.saturated_fats_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.saturated_fats_g), 20)}%</span>
            </div>
            <div className="flex justify-between py-1 pl-4">
              <span>Trans Fat {getNumericValue(nutrition.trans_fatty_acids_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.trans_fatty_acids_g), 2)}%</span>
            </div>
          </div>

          <div className="flex justify-between border-b border-black py-1">
            <span>Cholesterol {getNumericValue(nutrition.cholesterol_mg)}mg</span>
            <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.cholesterol_mg), 300)}%</span>
          </div>

          <div className="flex justify-between border-b border-black py-1">
            <span>Sodium {getNumericValue(nutrition.sodium_mg)}mg</span>
            <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.sodium_mg), 2300)}%</span>
          </div>

          <div className="border-b border-black">
            <div className="flex justify-between py-1">
              <span>Total Carbohydrate {getNumericValue(nutrition.carbohydrate_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.carbohydrate_g), 275)}%</span>
            </div>
            <div className="flex justify-between py-1 pl-4">
              <span>Dietary Fiber {getNumericValue(nutrition.fiber_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.fiber_g), 28)}%</span>
            </div>
            <div className="flex justify-between py-1 pl-4">
              <span>Total Sugars {getNumericValue(nutrition.sugars_g)}g</span>
              <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.sugars_g), 50)}%</span>
            </div>
            {getNumericValue(nutrition.added_sugar_g) > 0 && (
              <div className="flex justify-between py-1 pl-4">
                <span>Includes Added Sugars {getNumericValue(nutrition.added_sugar_g)}g</span>
                <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.added_sugar_g), 50)}%</span>
              </div>
            )}
          </div>

          <div className="flex justify-between border-b-8 border-black py-1">
            <span>Protein {getNumericValue(nutrition.protein_g)}g</span>
            <span className="font-bold">{calculatePercentage(getNumericValue(nutrition.protein_g), 50)}%</span>
          </div>

          {nutrients.map(({ key, label, unit, dailyValue }) => {
            const value = getNumericValue(nutrition[key]);
            if (value > 0) {
              return (
                <div className="flex justify-between border-b border-black py-1" key={key}>
                  <span>{label} {value} {unit}</span>
                  <span className="font-bold">{calculatePercentage(value, dailyValue)}%</span>
                </div>
              );
            }
            return null;
          })}

          <div className="text-xs mt-2">
            * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
          </div>
        </Card>
      </div>
      <MobileNavigation />
    </div>
  );
};

export default NutritionFacts;